import React, { useEffect, useState } from 'react';
import {
  useParams,
  useHistory,
  Redirect,
  useLocation,
} from 'react-router';
import {
  Box, Typography, Button, IconButton,
} from '@mui/material';
import {
  AutoStories, AccountCircle, Edit, Close, Save, ArrowBackIos, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { format } from 'rut.js';
import {
  getEnrollmentSummaryByStudentId,
  putUpdateEnrollmentSummaryByStudentId,
  getStudentsFromRepresentative,
} from '../../requests/api/students';
import { validateEnrollmentData } from './validations';
import { getSubjects } from '../../requests/api/subjects';
import { getLocations } from '../../requests/api/sections';
import { getSchools, putFinishEnrollmentProcess } from '../../requests/api/enrollment';
import FinishEnrollmentModal from './components/FinishEnrollmentModal';
import { ReduxState } from '../../types';
import handleApiResponse from '../../utils/handleApiResponse';
import useStyles from './styles';
import ContentBox from './components/contentBox';

import {
  ContentBoxProps,
  AttributeItem,
  PersonInfo,
  EnrollmentData,
} from './types';
import { formatDateToString } from '../../utils/helpers';
import { COMMUNES, MARITAL_STATUS } from '../types';
import {
  STUDENT, REPRESENTATIVE, ADMINISTRATOR, SECRETARY, ACADEMIC, SUSTAINER, USER,
} from '../../utils/user_types';
import StudyPlan from '../StudyPlan';

function StudentInfo({
  tab,
  onTabChange,
  studentIdProp,
  skipAccessCheck = false,
}: {
  tab: 'informacion' | 'plan-estudios';
  onTabChange: (tab: 'informacion' | 'plan-estudios') => void;
  studentIdProp?: string;
  skipAccessCheck?: boolean;
}) {
  const history = useHistory();
  const location = useLocation<{ stage: string }>();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const account = useSelector((state: ReduxState) => state.account);
  const { studentId: studentIdFromParams } = useParams<{ studentId: string }>();
  const studentId = studentIdProp || studentIdFromParams;
  const [hasAccess, setHasAccess] = useState<boolean | null>(skipAccessCheck ? true : null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editData, setEditData] = useState<EnrollmentData | null>(null);
  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const [locations, setLocations] = useState<{ id: number; name: string; address: string }[]>([]);
  const [schools, setSchools] = useState<{ id: number; name: string }[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [finishModalOpen, setFinishModalOpen] = useState<boolean>(false);
  const [finishLoading, setFinishLoading] = useState<boolean>(false);

  const handleFieldChange = (
    path: string,
    value: string | number | Date | boolean | null | (string | number)[],
  ) => {
    if (!editData) return;

    setEditData((prev) => {
      if (!prev) return null;

      const newData: EnrollmentData = { ...prev };
      const pathParts = path.split('.');
      // eslint-disable-next-line
      let current: any = newData;

      for (let i = 0; i < pathParts.length - 1; i += 1) {
        const part = pathParts[i];
        if (i === 0 && part === STUDENT) {
          current.student = { ...current.student };
          if (!current.student.user) current.student.user = { ...current.student.user };
          current = current.student;
        } else if (i === 0 && part === REPRESENTATIVE) {
          current.representative = {
            ...current.representative,
            user: { ...current.representative.user },
          };
          current = current.representative;
        } else if (i === 0 && part === SUSTAINER) {
          current.sustainer = { ...current.sustainer, user: { ...current.sustainer.user } };
          current = current.sustainer;
        } else if (part === USER) {
          current.user = { ...current.user };
          current = current.user;
        } else {
          current = current[part];
        }
      }

      const lastKey = pathParts[pathParts.length - 1];
      current[lastKey] = value;

      return newData;
    });
  };

  const buildPersonAttributes = (
    person: PersonInfo,
    options: {
      includeNacionalidad?: boolean;
      includeColegio?: boolean;
      includeDireccionBloque?: boolean;
      school?: string | null;
      onChangePath?: string;
    } = {},
  ): AttributeItem[] => {
    const path = options.onChangePath || 'student';
    const attributes: AttributeItem[] = [
      {
        title: 'Nombre',
        value: person.user?.name || '',
        type: 'text',
        error: validationErrors[`${path}.user.name`],
        onChange: (value) => {
          handleFieldChange(`${path}.user.name`, value as string);

          if (validationErrors[`${path}.user.name`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.user.name`];
              return newErrors;
            });
          }
        },
      },
      {
        title: 'Apellidos',
        value: person.user?.lastname || '',
        type: 'text',
        error: validationErrors[`${path}.user.lastname`],
        onChange: (value) => {
          handleFieldChange(`${path}.user.lastname`, value as string);

          if (validationErrors[`${path}.user.lastname`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.user.lastname`];
              return newErrors;
            });
          }
        },
      },
      {
        title: 'RUT',
        value: person.rut ? format(person.rut) : '',
        type: 'rut',
        error: validationErrors[`${path}.rut`],
        onChange: (value) => {
          handleFieldChange(`${path}.rut`, value as string);

          if (validationErrors[`${path}.rut`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.rut`];
              return newErrors;
            });
          }
        },
      },
      {
        title: 'Fecha de nacimiento',
        value: (() => {
          if (isEditMode) {
            return person.birthDate ? new Date(person.birthDate) : new Date();
          }
          return person.birthDate ? formatDateToString(person.birthDate) : '';
        })(),
        type: 'date',
        error: validationErrors[`${path}.birthDate`],
        onChange: (value) => {
          const dateString = value instanceof Date ? value.toISOString().split('T')[0] : value as string;
          handleFieldChange(`${path}.birthDate`, dateString);

          if (validationErrors[`${path}.birthDate`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.birthDate`];
              return newErrors;
            });
          }
        },
      },
      {
        title: 'Correo',
        value: person.user?.email || '',
        type: 'email',
        error: validationErrors[`${path}.user.email`],
        onChange: (value) => {
          handleFieldChange(`${path}.user.email`, value as string);

          if (validationErrors[`${path}.user.email`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.user.email`];
              return newErrors;
            });
          }
        },
      },
      {
        title: 'Teléfono',
        value: person.phoneNumber || '',
        type: 'text',
        error: validationErrors[`${path}.phoneNumber`],
        onChange: (value) => {
          handleFieldChange(`${path}.phoneNumber`, value as string);

          if (validationErrors[`${path}.phoneNumber`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.phoneNumber`];
              return newErrors;
            });
          }
        },
      },
    ];

    if (options.includeNacionalidad && (isEditMode || person.nationality)) {
      attributes.push({
        title: 'Nacionalidad',
        value: person.nationality || '',
        type: 'select',
        options: [{ label: 'Chilena', value: 'Chilena' }, { label: 'Extranjera', value: 'Extranjera' }],
        error: validationErrors[`${path}.nationality`],
        onChange: (value) => {
          handleFieldChange(`${path}.nationality`, value as string);

          if (validationErrors[`${path}.nationality`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.nationality`];
              return newErrors;
            });
          }
        },
      });
    }
    if (options.includeColegio) {
      attributes.push({
        title: 'Colegio',
        value: (() => {
          if (isEditMode) {
            return editData?.student.schoolId || '';
          }
          // Si es "Otro" y hay otherSchoolName, mostrar "Otro (nombre)"
          const schoolName = options.school || '';
          if (schoolName.toLowerCase() === 'otro' && editData?.student.otherSchoolName) {
            return `Otro (${editData.student.otherSchoolName})`;
          }
          return schoolName;
        })(),
        type: isEditMode ? 'autocomplete' : 'text',
        options: isEditMode ? schools.map((school) => ({
          label: school.name,
          value: school.id,
        })) : undefined,
        error: validationErrors[`${path}.schoolId`],
        onChange: (value) => {
          const schoolId = typeof value === 'number' ? value : Number(value);
          handleFieldChange(`${path}.schoolId`, schoolId);
          const selectedSchool = schools.find((s) => s.id === schoolId);
          if (selectedSchool) {
            handleFieldChange(`${path}.school`, selectedSchool.name);
          }
          if (validationErrors[`${path}.schoolId`]) {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[`${path}.schoolId`];
              return newErrors;
            });
          }
        },
      });

      // Campo adicional para "Otro colegio" - solo en modo edición
      if (isEditMode) {
        const selectedSchool = schools.find((s) => s.id === editData?.student.schoolId);
        const isOtherSchool = selectedSchool?.name.toLowerCase() === 'otro';

        if (isOtherSchool) {
          attributes.push({
            title: 'Nombre del otro colegio',
            value: editData?.student.otherSchoolName || '',
            type: 'text',
            error: validationErrors[`${path}.otherSchoolName`],
            onChange: (value) => {
              handleFieldChange(`${path}.otherSchoolName`, value as string);
              if (validationErrors[`${path}.otherSchoolName`]) {
                setValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[`${path}.otherSchoolName`];
                  return newErrors;
                });
              }
            },
          });
        }
      }
    }
    if (options.includeDireccionBloque) {
      if (isEditMode || person.maritalStatus) {
        attributes.push({
          title: 'Estado civil',
          value: person.maritalStatus || '',
          type: 'select',
          options: MARITAL_STATUS.map((status) => ({ label: status.name, value: status.id })),
          error: validationErrors[`${path}.maritalStatus`],
          onChange: (value) => {
            handleFieldChange(`${path}.maritalStatus`, value as string);

            if (validationErrors[`${path}.maritalStatus`]) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[`${path}.maritalStatus`];
                return newErrors;
              });
            }
          },
        });
      }
      if (isEditMode || person.address) {
        attributes.push({
          title: 'Dirección',
          value: person.address || '',
          type: 'text',
          error: validationErrors[`${path}.address`],
          onChange: (value) => {
            handleFieldChange(`${path}.address`, value as string);

            if (validationErrors[`${path}.address`]) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[`${path}.address`];
                return newErrors;
              });
            }
          },
        });
      }
      if (isEditMode || person.commune) {
        attributes.push({
          title: 'Comuna',
          value: (() => {
            if (isEditMode) {
              return person.commune || '';
            }
            // Si es "Otro" y hay otherCommune, mostrar "Otro (nombre)"
            const communeValue = person.commune || '';
            if (communeValue.toLowerCase() === 'otro' && person.otherCommune) {
              return `Otro (${person.otherCommune})`;
            }
            // Si no tiene otherCommune pero es "otro", mostrar "Otro"
            if (communeValue.toLowerCase() === 'otro') {
              return 'Otro';
            }
            return communeValue;
          })(),
          type: isEditMode ? 'autocomplete' : 'text',
          options: isEditMode ? [
            ...COMMUNES
              .map((commune) => ({ label: commune.name, value: commune.id }))
              .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
            { label: 'Otro', value: 'otro' },
          ] : undefined,
          error: validationErrors[`${path}.commune`],
          onChange: (value) => {
            handleFieldChange(`${path}.commune`, value as string);

            if (validationErrors[`${path}.commune`]) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[`${path}.commune`];
                return newErrors;
              });
            }
          },
        });

        // Campo adicional para "Otra comuna" - solo en modo edición
        if (isEditMode && person.commune?.toLowerCase() === 'otro') {
          attributes.push({
            title: 'Nombre de la otra comuna',
            value: person.otherCommune || '',
            type: 'text',
            error: validationErrors[`${path}.otherCommune`],
            onChange: (value) => {
              handleFieldChange(`${path}.otherCommune`, value as string);
              if (validationErrors[`${path}.otherCommune`]) {
                setValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[`${path}.otherCommune`];
                  return newErrors;
                });
              }
            },
          });
        }
      }
    }

    return attributes;
  };

  useEffect(() => {
    if (skipAccessCheck) {
      return;
    }

    const checkAccess = async () => {
      if (!account?.user || !studentId) {
        setHasAccess(false);
        return;
      }

      if (
        account.user.userType === ADMINISTRATOR
        || account.user.userType === SECRETARY
        || account.user.userType === ACADEMIC
      ) {
        setHasAccess(true);
        return;
      }

      if (account.user.userType === STUDENT) {
        const hasAccessToStudent = account.user.userTypeId?.toString() === studentId;
        setHasAccess(hasAccessToStudent);
        return;
      }

      if (account.user.userType === REPRESENTATIVE) {
        try {
          const { students } = await getStudentsFromRepresentative(
            account.user.userTypeId,
          );
          const studentIds = students.map(
            (s: { studentId: number }) => s.studentId.toString(),
          );
          const hasAccessToStudent = studentIds.includes(studentId);
          setHasAccess(hasAccessToStudent);
        } catch (err) {
          setHasAccess(false);
        }
        return;
      }

      setHasAccess(false);
    };

    checkAccess();
  }, [account, studentId, skipAccessCheck]);

  const handleFinishEnrollment = async () => {
    if (!studentId) return;

    try {
      setFinishLoading(true);
      const response = await putFinishEnrollmentProcess(studentId);
      handleApiResponse(enqueueSnackbar, response, true);
      setFinishModalOpen(false);

      history.push('/tabla-registro');
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setFinishLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!studentId || hasAccess === null) {
        return;
      }

      if (!hasAccess) {
        setLoading(false);
        return;
      }

      try {
        const [enrollmentData,
          subjectsData,
          locationsData,
          schoolsData,
        ] = await Promise.all([
          getEnrollmentSummaryByStudentId(studentId),
          getSubjects(),
          getLocations(),
          getSchools(),
        ]);
        const duplicatedData = { ...enrollmentData } as EnrollmentData;
        const rep = duplicatedData.representative;
        const sus = duplicatedData.sustainer;
        if (rep && !sus) {
          duplicatedData.sustainer = JSON.parse(JSON.stringify(rep));
        } else if (!rep && sus) {
          duplicatedData.representative = JSON.parse(JSON.stringify(sus));
        }

        setEditData(duplicatedData);
        setSubjects(subjectsData.subjects || []);
        setLocations((locationsData.locations || []).filter((loc: {
          id: number;
          name: string;
          address: string;
        }) => !loc.name.toLowerCase().includes('online')));
        setSchools(schoolsData.schools || []);
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }
      setLoading(false);
    };
    fetchData();
  }, [studentId, hasAccess]);

  const sections: ContentBoxProps[] = !editData ? [] : [
    {
      type: 'withAttributes',
      title: 'Datos del alumno',
      listOfAttributes: buildPersonAttributes(editData.student, {
        includeColegio: true,
        school: editData.student.school,
        onChangePath: 'student',
      }),
    },
    ...(editData.representative ? [{
      type: 'withAttributes' as const,
      title: 'Datos del apoderado',
      listOfAttributes: buildPersonAttributes(editData.representative, {
        includeNacionalidad: true,
        includeDireccionBloque: true,
        onChangePath: 'representative',
      }),
      isEditMode,
    }] as ContentBoxProps[] : []),
    ...(editData.sustainer ? [{
      type: 'withAttributes' as const,
      title: 'Datos del sostenedor',
      listOfAttributes: buildPersonAttributes(editData.sustainer, {
        includeNacionalidad: true,
        includeDireccionBloque: true,
        onChangePath: 'sustainer',
      }),
      isEditMode,
      samePersonCheckbox: isEditMode ? {
        label: 'Es el mismo que el apoderado',
        checked: (() => {
          const rep = editData.representative;
          const sus = editData.sustainer;
          if (!rep || !sus) return false;
          const repRut = (rep.rut || '').replace(/\.|-/g, '');
          const susRut = (sus.rut || '').replace(/\.|-/g, '');
          const sameRut = repRut && susRut && repRut === susRut;
          const sameEmail = rep.user?.email && sus.user?.email
            && rep.user.email.toLowerCase().trim() === sus.user.email.toLowerCase().trim();
          return Boolean(sameRut || sameEmail);
        })(),
        onChange: (checked: boolean) => {
          setEditData((prev) => {
            if (!prev) return null;
            if (checked) {
              if (prev.representative) {
                return {
                  ...prev,
                  sustainer: JSON.parse(JSON.stringify(prev.representative)),
                };
              }
              return prev;
            }
            return {
              ...prev,
              sustainer: prev.sustainer ? {
                ...prev.sustainer,
                rut: '',
                phoneNumber: '',
                birthDate: '',
                nationality: '',
                maritalStatus: '',
                address: '',
                commune: '',
                otherCommune: '',
                user: {
                  id: prev.sustainer.user?.id || 0,
                  name: '',
                  lastname: '',
                  email: '',
                },
              } : prev.sustainer,
            };
          });
        },
      } : undefined,
    }] as ContentBoxProps[] : []),
    {
      type: 'withMultipleText',
      sections: [
        {
          title: 'Áreas de interés',
          textContent: (editData.student.testAreas || []).join(', '),
          isEditable: true,
          editType: 'checkbox',
          subjects,
          selectedSubjectIds: (() => editData.student.testAreas || [])(),
          onChange: (selectedIds: string | string[]) => {
            const ids = Array.isArray(selectedIds) ? selectedIds : [];
            setEditData((prev) => {
              if (!prev) return null;
              return {
                ...prev,
                student: {
                  ...prev.student,
                  testAreas: ids,
                },
              };
            });
          },
        },
        {
          title: 'Objetivos',
          textContent: editData.student.comment || '',
          isEditable: true,
          editType: 'textarea',
          onChange: (value: string | string[]) => {
            const strValue = typeof value === 'string' ? value : '';
            handleFieldChange('student.comment', strValue);
          },
        },
      ],
      isEditMode,
    },
    {
      type: 'withAttributes',
      title: 'Sedes y nivel',
      listOfAttributes: [
        {
          title: 'Sede(s)',
          value: (() => {
            const hasPreferredArray = Array.isArray(editData.student.preferredLocationIds)
              && editData.student.preferredLocationIds.length > 0;

            let preferredIds: number[] = [];
            if (hasPreferredArray && editData.student.preferredLocationIds) {
              preferredIds = editData.student.preferredLocationIds;
            } else if (editData.student.locationId) {
              preferredIds = [editData.student.locationId];
            }

            if (isEditMode) {
              return preferredIds;
            }

            const selectedLocations = locations
              .filter((loc) => preferredIds.some((id) => id === loc.id));

            if (selectedLocations.length === 0) {
              return editData.student.location ? [`- ${editData.student.location}`] : [];
            }

            return selectedLocations.map((loc) => `- ${loc.name}`);
          })(),
          type: 'multiselect',
          options: locations.map((l) => ({
            label: l.name,
            value: l.id,
          })),
          error: validationErrors['student.locationId'],
          onChange: (value: string | number | Date | boolean | (string | number)[]) => {
            const raw = Array.isArray(value) ? value : [value];
            const numericIds = raw
              .map((v) => (v !== null && v !== undefined ? Number(v) : null))
              .filter((v): v is number => Number.isInteger(v));

            const primaryLocationId = numericIds.length > 0 ? numericIds[0] : null;

            handleFieldChange('student.locationId', primaryLocationId as number | null);
            handleFieldChange('student.preferredLocationIds', numericIds);

            const selectedLocations = locations
              .filter((loc) => numericIds.some((id) => id === loc.id));
            if (selectedLocations.length > 0) {
              const names = selectedLocations.map((loc) => loc.name).join(', ');
              handleFieldChange('student.location', names);
            } else {
              handleFieldChange('student.location', '');
            }

            if (validationErrors['student.locationId']) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors['student.locationId'];
                return newErrors;
              });
            }
          },
        },
        {
          title: 'Nivel a cursar',
          value: editData.student.classYear,
          type: 'select',
          options: [
            { label: 'IV Medio', value: 'IV Medio' },
            { label: 'III Medio', value: 'III Medio' },
            { label: 'Egresados', value: 'Egresados' },
          ],
          error: validationErrors['student.classYear'],
          onChange: (value) => {
            handleFieldChange('student.classYear', value as string);

            if (validationErrors['student.classYear']) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors['student.classYear'];
                return newErrors;
              });
            }
          },
        },
        {
          title: 'Año',
          value: editData.student.inscriptionYear ?? '',
          type: 'year',
          error: validationErrors['student.inscriptionYear'],
          onChange: (value) => {
            const parsedValue = typeof value === 'number' ? value : null;
            handleFieldChange('student.inscriptionYear', parsedValue);

            if (validationErrors['student.inscriptionYear']) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors['student.inscriptionYear'];
                return newErrors;
              });
            }
          },
        },
      ],
      isEditMode,
    },
    {
      type: 'withAttributes',
      title: '¿Cómo nos conociste?',
      listOfAttributes: [
        {
          title: '¿Cómo nos conociste?',
          value: editData.student.leadSource || '',
          type: 'select' as const,
          options: [
            { label: 'Recomendación de un amigo o familiar', value: 'recomendacion' },
            { label: 'Hermano(a)', value: 'hermano' },
            { label: 'Página web', value: 'pagina_web' },
            { label: 'Embajador(a)', value: 'embajador' },
            { label: 'Instagram', value: 'instagram' },
            { label: 'Profesor(a)', value: 'profesor' },
            { label: 'Centro de alumnos', value: 'centro_alumnos' },
          ],
          error: validationErrors['student.leadSource'],
          onChange: (value) => {
            const newLeadSource = value as string;
            handleFieldChange('student.leadSource', newLeadSource);

            // Limpiar campos que ya no aplican según la nueva selección
            if (newLeadSource !== 'hermano' && newLeadSource !== 'profesor') {
              handleFieldChange('student.leadName', '');
            }
            if (newLeadSource !== 'embajador' && newLeadSource !== 'centro_alumnos') {
              handleFieldChange('student.ambassadorCode', '');
            }

            if (validationErrors['student.leadSource']) {
              setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors['student.leadSource'];
                delete newErrors['student.leadName'];
                delete newErrors['student.ambassadorCode'];
                return newErrors;
              });
            }
          },
        },
        ...((isEditMode
          && (editData.student.leadSource === 'hermano'
            || editData.student.leadSource === 'profesor'))
          || (!isEditMode && editData.student.leadName)
          ? [{
            title: 'leadName',
            value: editData.student.leadName || '',
            type: 'text' as const,
            error: validationErrors['student.leadName'],
            onChange: (value: string | number | Date | boolean | (string | number)[]) => {
              handleFieldChange('student.leadName', value);

              if (validationErrors['student.leadName']) {
                setValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors['student.leadName'];
                  return newErrors;
                });
              }
            },
          }] : []),
        ...((isEditMode
          && (editData.student.leadSource === 'embajador'
            || editData.student.leadSource === 'centro_alumnos'))
          || (!isEditMode && editData.student.ambassadorCode)
          ? [{
            title: 'ambassadorCode',
            value: editData.student.ambassadorCode || '',
            type: 'text' as const,
            error: validationErrors['student.ambassadorCode'],
            onChange: (value: string | number | Date | boolean | (string | number)[]) => {
              handleFieldChange('student.ambassadorCode', value);

              if (validationErrors['student.ambassadorCode']) {
                setValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors['student.ambassadorCode'];
                  return newErrors;
                });
              }
            },
          }] : []),
      ],
      isEditMode,
      leadSource: editData.student.leadSource,
    },
  ];

  const renderTabContent = () => {
    if (tab === 'informacion') {
      return (
        <Box>
          <Box className={classes.mainContainer}>
            {!loading && sections.map((section, index) => (
              <ContentBox
                key={index}
                {...section}
                isEditMode={isEditMode}
              />
            ))}
          </Box>
          <Box className={classes.buttonsContainer}>
            {account?.user?.userType !== REPRESENTATIVE
              && account?.user?.userType !== STUDENT
              && editData?.student?.stage !== 'closed'
              && (
                <Button
                  className={classes.finishButton}
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setFinishModalOpen(true)}
                >
                  Alumno no interesado
                </Button>
              )}
            {account?.user?.userType !== REPRESENTATIVE && account?.user?.userType !== STUDENT && (
              <Box className={classes.editButtonContainer}>
                {isEditMode ? (
                  <>
                    <Button
                      className={classes.editButton}
                      variant="outlined"
                      startIcon={<Close />}
                      onClick={() => setIsEditMode(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      className={`${classes.editButton} ${classes.successButton}`}
                      variant="contained"
                      startIcon={<Save />}
                      onClick={async () => {
                        if (!editData) return;

                        const validation = validateEnrollmentData(editData);

                        if (!validation.isValid) {
                          const errorsMap: Record<string, string> = {};
                          validation.errors.forEach((error) => {
                            errorsMap[error.field] = error.message;
                          });

                          setValidationErrors(errorsMap);

                          const firstErrorMessage = validation.errors[0]?.message || 'Error de validación';
                          enqueueSnackbar(firstErrorMessage, {
                            variant: 'error',
                            autoHideDuration: 6000,
                          });
                          return;
                        }

                        setValidationErrors({});

                        try {
                          const payload = {
                            student: {
                              rut: editData.student.rut,
                              phoneNumber: editData.student.phoneNumber,
                              birthDate: editData.student.birthDate,
                              preferredLocationIds: (() => {
                                if (Array.isArray(editData.student.preferredLocationIds)) {
                                  return editData.student.preferredLocationIds;
                                }
                                if (editData.student.locationId) {
                                  return [editData.student.locationId];
                                }
                                return [];
                              })(),
                              desiredClassYear: editData.student.classYear,
                              inscriptionYear: editData.student.inscriptionYear ?? null,
                              leadSource: editData.student.leadSource,
                              leadName: editData.student.leadName,
                              ambassadorCode: editData.student.ambassadorCode,
                              schoolId: editData.student.schoolId,
                              otherSchoolName: editData.student.otherSchoolName ?? null,
                              testsToTake: editData.student.testAreas,
                              testsComment: editData.student.comment,
                              user: {
                                email: editData.student.user.email,
                                name: editData.student.user.name,
                                lastname: editData.student.user.lastname,
                              },
                            },
                            representative: editData.representative ? {
                              rut: editData.representative.rut,
                              phoneNumber: editData.representative.phoneNumber,
                              birthDate: editData.representative.birthDate,
                              nationality: editData.representative.nationality,
                              maritalStatus: editData.representative.maritalStatus,
                              address: editData.representative.address,
                              commune: editData.representative.commune,
                              otherCommune: editData.representative.otherCommune ?? null,
                              name: editData.representative.user.name,
                              lastname: editData.representative.user.lastname,
                              email: editData.representative.user.email,
                            } : null,
                            sustainer: editData.sustainer ? {
                              rut: editData.sustainer.rut,
                              phoneNumber: editData.sustainer.phoneNumber,
                              birthDate: editData.sustainer.birthDate,
                              nationality: editData.sustainer.nationality,
                              maritalStatus: editData.sustainer.maritalStatus,
                              address: editData.sustainer.address,
                              commune: editData.sustainer.commune,
                              otherCommune: editData.sustainer.otherCommune ?? null,
                              name: editData.sustainer.user.name,
                              lastname: editData.sustainer.user.lastname,
                              email: editData.sustainer.user.email,
                            } : null,
                          };

                          await putUpdateEnrollmentSummaryByStudentId(studentId, payload);
                          enqueueSnackbar('Datos guardados correctamente', { variant: 'success' });
                          setIsEditMode(false);
                        } catch (err) {
                          const e = err as AxiosError;
                          handleApiResponse(enqueueSnackbar, e, false);
                        }
                      }}
                    >
                      Guardar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      className={classes.editButton}
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => setIsEditMode(true)}
                    >
                      Editar info
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    if (tab === 'plan-estudios') {
      return (
        <StudyPlan
          studentIdProp={studentId}
          studentStage={editData?.student?.stage ?? undefined}
        />
      );
    }

    return null;
  };

  const studentFullName = editData?.student?.user
    ? `${editData.student.user.name} ${editData.student.user.lastname}`.trim()
    : '';

  // Mostrar el botón de volver solo cuando realmente venimos desde la tabla de registro
  // (ruta /tabla-registro/alumno/:studentId), para evitar duplicar flechas en otras vistas.
  const isFromRegisterTable = location.pathname.startsWith('/tabla-registro');

  if (hasAccess === false) {
    return <Redirect to="/sin-autorizacion" />;
  }

  return (
    <Box className={classes.studentInfoContainer}>
      <Box className={classes.headerContainer}>
        {isFromRegisterTable && (
          <Box className={classes.backButtonContainer}>
            <IconButton
              onClick={() => history.push('/tabla-registro')}
              className={classes.backButton}
              size="small"
            >
              <ArrowBackIos className={classes.backIcon} />
            </IconButton>
            {studentFullName && (
              <Typography className={classes.studentName}>
                {studentFullName}
              </Typography>
            )}
          </Box>
        )}
        {!isFromRegisterTable && <Box />}
        <Box className={classes.tabsContainer}>
          <Button
            className={`${classes.tabButton} ${tab === 'plan-estudios' ? classes.tabButtonActive : classes.tabButtonInactive}`}
            onClick={() => onTabChange('plan-estudios')}
            startIcon={<AutoStories className={classes.tabButtonIcon} />}
          >
            Plan de estudios
            {location?.state?.stage === 'answer_pending' && <Box className={classes.pendingDot} />}
          </Button>
          <Button
            className={`${classes.tabButton} ${tab === 'informacion' ? classes.tabButtonActive : classes.tabButtonInactive}`}
            onClick={() => onTabChange('informacion')}
            startIcon={<AccountCircle className={classes.tabButtonIcon} />}
          >
            Información
          </Button>
        </Box>
      </Box>
      {renderTabContent()}
      <FinishEnrollmentModal
        open={finishModalOpen}
        onClose={() => setFinishModalOpen(false)}
        onConfirm={handleFinishEnrollment}
        loading={finishLoading}
      />
    </Box>
  );
}

export default StudentInfo;
